import React from 'react';
import * as Yup from 'yup';
import { Formik } from 'formik';
import { Box, Typography } from '@mui/material';
import { ButtonStyleCheckbox, ButtonStyleCheckboxGroup } from '@uplift/ui-v2';
import { FormFooter } from '../components/FormFooter';
import { RematchingContainer } from '../components/RematchingContainer';
import { useQuery } from '@tanstack/react-query';
import { getRematchReasons } from '@uplift/api';
import { ProviderMedicalRole } from '@uplift/types';
import { useL10n } from '@uplift/hooks';

const validationSchema = Yup.object().shape({
  rematchReasons: Yup.array(Yup.string()).min(1).required(),
});

export type RematchingWhyPageProps = {
  onBack?: () => void;
  onNext: (reasons: string[]) => void;
  rematchReasons: string[];
  medicalRole: ProviderMedicalRole;
};

export const RematchingWhyPage: React.VFC<RematchingWhyPageProps> = props => {
  const l10n = useL10n();
  const roleLabel = l10n.resources.provider.roles[props.medicalRole].toLowerCase();
  const { data: options = [] } = useQuery({
    queryFn: () => getRematchReasons(props.medicalRole),
    queryKey: [ 'rematch-reasons', props.medicalRole ],
    select: data => data.rematchReasons,
  });

  return (
    <RematchingContainer>
      <Typography variant="h3">Why are you looking for<br />a new {roleLabel}?</Typography>
      <Box mt={2}>
        <Typography variant="h4">Knowing what isn&apos;t working is just as important as knowing what is when it comes to finding the right {roleLabel}.</Typography>
      </Box>
      <Formik
        initialValues={{ rematchReasons: props.rematchReasons }}
        validateOnMount
        validationSchema={validationSchema}
        onSubmit={v => props.onNext(v.rematchReasons)}
      >
        {(formik) => (
          <>
            <Box mt={4}>
              <ButtonStyleCheckboxGroup label="Select all that apply" name="rematchReasons">
                {options.map((item) => <ButtonStyleCheckbox key={item.id} value={item.name} label={item.description} />)}
              </ButtonStyleCheckboxGroup>
            </Box>
            <FormFooter
              nextDisabled={!formik.isValid}
              onBack={props.onBack}
              onNext={formik.handleSubmit}
            />
          </>
        )}
      </Formik>
    </RematchingContainer>
  );
};
